import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Breadcrumbs as MuiBreadcrumbs, CSSObject, Link, SxProps, Theme, Typography } from "@mui/material";
import { brandColors } from "../../../stories/helpers/ColorTokenHelper";
import { ColorTokensEnum } from "../../../enums/tokens/colorTokensEnum";
import { TypographyTokensEnum } from "../../../enums/tokens/typographyTokensEnum";
import typographyTokens from "../../../stories/tokens/typography-tokens.json";

export interface BreadcrumbItem {
  label: string;
  /**
   * Router path, relative to the router `basename` (e.g. `/profile`, not
   * `/lbpcustomer/profile`). The last item is the current page and is always
   * rendered as plain text, whether or not it has one.
   */
  to?: string;
}

export interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  sx?: SxProps<Theme>;
}

/**
 * Matches the hand-built `<Breadcrumbs>` block on the Builder.io IMPACT pages
 * (`/impact-programs/loyalty-link`, `/impact-programs/impact-rewards`), so a
 * trail reads the same whether Builder.io or React renders the page: Body 1
 * (Gotham-Book 16px), always-underlined interactive links, a "/" separator
 * centred in a 23px slot, and 24px above and below. The two greys are the
 * Builder.io values; the palette has no token for either.
 */
const BODY_1 = (typographyTokens[TypographyTokensEnum.BODY_1] || {}) as CSSObject;
const LINK_COLOR = brandColors[ColorTokensEnum.INTERACTIVE];
const TEXT_COLOR = "rgba(0, 0, 0, 0.87)";
const SEPARATOR_COLOR = "#555555";
const SEPARATOR_WIDTH_PX = 23;
const MARGIN_Y_PX = 24;

/**
 * The storefront breadcrumb trail. Links navigate inside the router, so a
 * crumb never reloads the app; callers pass the full trail, Home included.
 */
export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items, sx }) => (
  <MuiBreadcrumbs
    separator="/"
    aria-label="breadcrumb"
    sx={{
      ...BODY_1,
      my: `${MARGIN_Y_PX}px`,
      "& .MuiBreadcrumbs-separator": {
        mx: 0,
        width: `${SEPARATOR_WIDTH_PX}px`,
        justifyContent: "center",
        color: SEPARATOR_COLOR,
      },
      ...sx,
    }}
  >
    {items.map((item, index) =>
      item.to && index < items.length - 1 ? (
        <Link
          key={`${index}-${item.label}`}
          component={RouterLink}
          to={item.to}
          underline="always"
          sx={{
            ...BODY_1,
            color: LINK_COLOR,
            textDecorationColor: "currentColor",
          }}
        >
          {item.label}
        </Link>
      ) : (
        <Typography
          key={`${index}-${item.label}`}
          aria-current={index === items.length - 1 ? "page" : undefined}
          sx={{ ...BODY_1, color: TEXT_COLOR }}
        >
          {item.label}
        </Typography>
      )
    )}
  </MuiBreadcrumbs>
);

export default Breadcrumbs;
